import React, { useRef, useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { KeenIcon } from '@/components';
import { faImage, faPaperclip, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FaTimesCircle } from 'react-icons/fa';
import { useAuth } from '@/hooks/useAuth';
import { useGetAgent } from '@/hooks/useAgent';
import { useUploadThumb } from '@/hooks/useUploadFile';
import { UpdateAgentMutation } from '@/hooks/useAgentBuilder';
import { Checkbox, Modal } from 'antd';

const IntroImage = ({ onChange, introImageSettings }) => {
  const fileInputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isRemoved, setIsRemoved] = useState(false);
  const [enableIntroImage, setEnableIntroImage] = useState(false);
  const [hovered, setHovered] = useState(false);
  const intl = useIntl();
  const { userData } = useAuth()
  const { data: agent, isLoading } = useGetAgent(userData?.uID);
  const uploadThumb = useUploadThumb();
  const updateAgentMutation = UpdateAgentMutation();

  // Nếu chưa có previewUrl, lấy ảnh mặc định từ agent?.data?.intro_image_url, trừ khi đã xoá
  React.useEffect(() => {
    if (!previewUrl && agent?.data?.intro_image_url && !isRemoved) {
      setPreviewUrl(agent.data.intro_image_url);
    }
  }, [previewUrl, agent?.data?.intro_image_url, isRemoved]);

  // Reset hover state khi previewUrl thay đổi
  React.useEffect(() => {
    if (!previewUrl) {
      setHovered(false);
    }
  }, [previewUrl]);

  // Gửi enableIntroImage lên cha mỗi khi thay đổi
  React.useEffect(() => {
    // Chỉ gọi onChange khi thực sự có thay đổi
    if (onChange) {
      onChange({ enableIntroImage, introImageUrl: previewUrl });
    }
  }, [enableIntroImage, previewUrl]);

  React.useEffect(() => {
    if (typeof agent?.data?.intro_image_state !== 'undefined') {
      setEnableIntroImage(agent.data.intro_image_state === 1);
    }
  }, [agent?.data?.intro_image_state]);

  // Reset enableIntroImage khi prop đổi (sau khi save)
  React.useEffect(() => {
    if (introImageSettings && typeof introImageSettings.enableIntroImage !== 'undefined') {
      setEnableIntroImage(introImageSettings.enableIntroImage);
    }
  }, [introImageSettings?.enableIntroImage]);

  const handleCheckboxChange = (e) => {
    setEnableIntroImage(e.target.checked);
    onChange?.({ enableIntroImage: e.target.checked });
  };

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        Modal.error({
          title: intl.formatMessage({ id: "AGENTS.HOME.INVALID_FILE_TYPE" }),
          content: intl.formatMessage({ id: "AGENTS.HOME.INVALID_FILE_TYPE_MESSAGE" }),
          okText: intl.formatMessage({ id: "AGENTS.HOME.OK" }),
        });
        return;
      }

      // Validate file size (2048KB = 2MB)
      const maxSize = 2048 * 1024; // 2048KB in bytes
      if (file.size > maxSize) {
        Modal.error({
          title: intl.formatMessage({ id: "AGENTS.HOME.FILE_TOO_LARGE" }),
          content: intl.formatMessage({ id: "AGENTS.HOME.FILE_TOO_LARGE_MESSAGE" }),
          okText: intl.formatMessage({ id: "AGENTS.HOME.OK" }),
        });
        return;
      }

      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
  
      Modal.confirm({
        title: intl.formatMessage({ id: 'AGENTS.HOME.CONFIRM_INTRO_IMAGE_TITLE' }),
        content: intl.formatMessage({ id: 'AGENTS.HOME.CONFIRM_INTRO_IMAGE_CONTENT' }),
        okText: intl.formatMessage({ id: 'AGENTS.HOME.CONFIRM' }),
        cancelText: intl.formatMessage({ id: 'AGENTS.HOME.CANCEL' }),
        okButtonProps: { 
          style: { backgroundColor: '#50b553', borderColor: '#50b553' },
          className: 'bg-[#50b553] border-[#50b553] hover:bg-[#50b553] hover:border-[#50b553]'
        },
        onOk() {
          uploadThumb.mutate(
            { file },
            {
              onError: () => {
                try { URL.revokeObjectURL(url); } catch (_) {}
                setPreviewUrl(null);
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
              },
            }
          );
        },
        onCancel() {
          setPreviewUrl(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        },
      });
    }
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    console.log('Remove button clicked');

    try {
      Modal.confirm({
        title: intl.formatMessage({ id: 'AGENTS.HOME.CONFIRM_INTRO_IMAGE_TITLE' }),
        content: intl.formatMessage({ id: 'AGENTS.HOME.CONFIRM_INTRO_IMAGE_CONTENT' }),
        okText: intl.formatMessage({ id: 'AGENTS.HOME.CONFIRM' }),
        cancelText: intl.formatMessage({ id: 'AGENTS.HOME.CANCEL' }),
        okButtonProps: { 
          style: { backgroundColor: '#50b553', borderColor: '#50b553' },
          className: 'bg-[#50b553] border-[#50b553] hover:bg-[#50b553] hover:border-[#50b553]'
        },
        onOk: () => {
          console.log('Modal OK clicked');
          // Xoá ảnh preview hiện tại sau khi xác nhận
          setPreviewUrl(null);
          setIsRemoved(true);

          // Reset input để có thể chọn lại ảnh vừa xoá
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }

          // Gọi API cập nhật để xoá intro_image_url trên server
          updateAgentMutation.mutate({ intro_image_url: null });
        },
      });
      console.log('Modal.confirm called');
    } catch (error) {
      console.error('Error with Modal.confirm:', error);
      // Fallback to window.confirm
      const userConfirmed = window.confirm('Bạn có chắc muốn xóa ảnh này không?');
      if (userConfirmed) {
        setPreviewUrl(null);
        setIsRemoved(true);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        // Gọi API cập nhật để xoá intro_image_url trên server
        updateAgentMutation.mutate({ intro_image_url: null });
      }
    }
  };

  return (
    <div className="mb-3">
      <label className="block text-sm font-bold mb-2"><FormattedMessage id="AGENTS.HOME.INTRO_IMAGE" /> <span className="text-xs text-gray-500"></span></label>
      <div className="flex items-center gap-1 mb-2">
        <Checkbox checked={enableIntroImage} onChange={handleCheckboxChange} id="enable-intro-image" className="mr-1 accent-gray-300"/>
        <label  className="text-sm"><FormattedMessage id="AGENTS.HOME.ENABLE_INTRO_IMAGE" /></label>
      </div>
      <div className="flex gap-2">
        <div className="border rounded p-1 flex items-center justify-center text-2xl text-gray-400 bg-white overflow-hidden relative">
          {previewUrl ? (
            <div
              className="relative size-24 flex items-center justify-center overflow-hidden"
              onMouseEnter={() => setHovered(true)}
              onMouseLeave={() => setHovered(false)}
            >
              <img src={previewUrl} alt="Preview" className={`object-contain w-full h-full transition duration-200 ${hovered ? 'opacity-50' : ''}`} />
              {hovered && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/50">
                  <button
                    type="button"
                    onClick={handleRemove}
                    className="rounded-full p-2 text-red-500 hover:bg-gray-100 transition shadow"
                    title={<FormattedMessage id="AGENTS.HOME.REMOVE_IMAGE" />}
                  >
                    <FaTimesCircle size={24} />
                  </button>
                </div>
              )}
            </div>
          ) : (
            <FontAwesomeIcon icon={faImage} className='size-8 p-7' />
          )}
        </div>
        <button
          type="button"
          className="flex-1 border rounded px-3 py-2 flex items-center gap-2 hover:bg-gray-100 transition-colors text-left bg-white "
          onClick={handleButtonClick}
        >
          <img src="/media/images/icon/parperclip.png" alt="paper clip" className='size-6' />
          <p className='text-sm'><FormattedMessage id="AGENTS.HOME.UPLOAD_FILE_IMAGE" /></p>
        </button>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept=".jpeg,.png,.jpg,.gif,.svg"
          onChange={handleFileChange}
        />
      </div>
    </div>
  );
};

export { IntroImage };
